import { useEffect, useRef, useState, type CSSProperties } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ClipboardList, Crown, ListOrdered, Ticket, type LucideIcon } from 'lucide-react';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatNumber, formatWeekday } from '../lib/format';
import { Badge, Card, ProgressBar, priorityTone } from './ui';
import tokens from '../../../../packages/design-tokens/design-tokens.json';

export type ProgressKind = 'TASK' | 'LEADER_TASK' | 'TICKET' | 'Q_TASK';

/** A piece of the reader's open work, as Up next names it. */
interface ProgressItem {
  kind: ProgressKind;
  id: string;
  /** A ticket's or a Q Task's number; tasks have none. */
  number: string | null;
  title: string;
  status: string;
  priority: string | null;
  dueAt: string | null;
  isOverdue: boolean;
  project: { id: string; name: string; nameAr: string | null; code: string } | null;
}

/** `progress` on `GET /dashboard`: the reader's own work today, as domain/work-progress.ts counts it. */
export interface WorkProgressBlock {
  /** The company's today, YYYY-MM-DD. */
  today: string;
  /** Finished today. */
  done: number;
  /** Still theirs to finish. */
  left: number;
  /** With someone else for now: sent for review before today, or waiting for the requester. */
  waiting: number;
  /** Not started, and planned to begin on a later day. */
  later: number;
  /** Of what is left, past its deadline. */
  overdue: number;
  /** Done out of done and left, rounded down; none when there was nothing to do. */
  percent: number | null;
  kinds: Array<{ kind: ProgressKind; done: number; left: number }>;
  /** What to finish next: the late first, then the most urgent, then the nearest deadline. */
  next: ProgressItem[];
  /** The last seven days, oldest first, today the last. */
  week: Array<{ date: string; finished: number }>;
}

const KIND_ICONS: Record<ProgressKind, LucideIcon> = {
  TASK: ClipboardList,
  LEADER_TASK: Crown,
  TICKET: Ticket,
  Q_TASK: ListOrdered,
};

/** Each kind by its menu's own name. */
const KIND_LABELS: Record<ProgressKind, TranslationKey> = {
  TASK: 'nav.tasks',
  LEADER_TASK: 'nav.leaderTasks',
  TICKET: 'nav.tickets',
  Q_TASK: 'nav.qTasks',
};

/** Where each kind's pieces open, under its list. */
const KIND_LINKS: Record<ProgressKind, string> = {
  TASK: '/tasks',
  LEADER_TASK: '/leader-tasks',
  TICKET: '/tickets',
  Q_TASK: '/q-tasks',
};

/** Each kind's list — the Tasks list at My tasks, which it takes from its address. */
const KIND_LISTS: Record<ProgressKind, string> = { ...KIND_LINKS, TASK: '/tasks?mine=true' };

type Stage = 'clear' | 'start' | 'going' | 'half' | 'almost' | 'done';

const STAGE_TITLES: Record<Stage, TranslationKey> = {
  clear: 'dash.progress.clear',
  start: 'dash.progress.start',
  going: 'dash.progress.going',
  half: 'dash.progress.half',
  almost: 'dash.progress.almost',
  done: 'dash.progress.done',
};

/** Where the day's work stands, in words: nothing to do, not begun, under way, past half, nearly, all done. */
function stageOf(block: WorkProgressBlock): Stage {
  if (block.percent === null) return 'clear';
  if (block.left === 0) return 'done';
  if (block.done === 0) return 'start';
  if (block.left === 1 || block.percent >= 85) return 'almost';
  return block.percent >= 50 ? 'half' : 'going';
}

/**
 * The reader's own progress through their work today, at the top of everyone's
 * dashboard (operator, 2026-11-02: "in dashboard i need progress to finish my
 * work creative way for everyone"). A ring of the day's work — a segment for
 * each piece, lit as it is finished — fills toward closing; beside it, where
 * the day stands in words, done and left by kind, what to finish next, and the
 * last seven days. It moves with the dashboard's 30-second refetch: a piece
 * finished in another tab lights its segment here. All of it finished, the ring
 * glows, and sends off a burst of sparks once.
 *
 * What counts — done, left, waiting on others, planned for later — is the
 * API's (domain/work-progress.ts); each figure's title says.
 */
export function WorkProgressCard({ block }: { block: WorkProgressBlock }) {
  const { t, locale } = useI18n();
  const n = (value: number) => formatNumber(value, locale);
  const stage = stageOf(block);
  const complete = stage === 'done';
  const total = block.done + block.left;

  const hint = {
    clear: block.waiting > 0 ? t('dash.progress.clearWaiting', { n: n(block.waiting) }) : t('dash.progress.clearHint'),
    start: t('dash.progress.startHint', { n: n(block.left) }),
    going: t('dash.progress.goingHint', { done: n(block.done), left: n(block.left) }),
    half: t('dash.progress.halfHint', { left: n(block.left) }),
    almost: t('dash.progress.almostHint', { left: n(block.left) }),
    done: t('dash.progress.doneHint'),
  }[stage];

  return (
    <section className="mb-8">
      <Card
        padded={false}
        className={clsx('relative overflow-hidden', complete && 'border-warning/50 dark:shadow-edge-signal')}
      >
        {/* The system reading the panel: one sweep when it arrives (Sentinel's `scan`), in grey. */}
        <span
          className="pointer-events-none absolute inset-x-0 top-0 h-[10%] border-b border-neutral-400/50 opacity-0 motion-safe:animate-scan"
          aria-hidden
        />
        {/* Side by side only where the ring's half still has room for its words: below
            that, the ring and its words on top, Up next and the week under them. */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <div className="flex flex-col items-center gap-5 p-5 sm:flex-row sm:items-center">
            <ProgressRing block={block} complete={complete} />

            <div className="min-w-0 flex-1 text-center sm:text-start">
              <h2 className="font-sans text-overline uppercase text-[var(--text-tertiary)] rtl:font-arabic">{t('dash.progress.title')}</h2>
              {/* Noon, so the company's day reads the same in every time zone. */}
              <p className="mt-0.5 font-mono text-mono-sm tabular text-faint">
                {formatWeekday(block.today, locale, 'long')} {formatDate(`${block.today}T12:00:00`, locale)}
              </p>
              <p className="mt-2 font-display text-h2 text-[var(--text-primary)] rtl:font-arabic">{t(STAGE_TITLES[stage])}</p>
              <p className="mt-0.5 text-body-sm text-muted">{hint}</p>

              {(total > 0 || block.waiting > 0 || block.later > 0) && (
                <ul className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                  {total > 0 && (
                    <>
                      <LegendChip
                        swatch={CARD_COLOURS.done}
                        label={t('dash.progress.legendDone')}
                        value={n(block.done)}
                        hint={t('dash.progress.legendDoneHint')}
                      />
                      <LegendChip
                        swatch={CARD_COLOURS.rest}
                        label={t('dash.progress.legendLeft')}
                        value={n(block.left)}
                        hint={t('dash.progress.legendLeftHint')}
                      />
                    </>
                  )}
                  {block.waiting > 0 && (
                    <LegendChip
                      swatch="border border-dashed border-neutral-400 dark:border-neutral-500"
                      label={t('dash.progress.legendWaiting')}
                      value={n(block.waiting)}
                      hint={t('dash.progress.legendWaitingHint')}
                    />
                  )}
                  {block.later > 0 && (
                    <LegendChip
                      swatch="border border-neutral-400 dark:border-neutral-500"
                      label={t('dash.progress.legendLater')}
                      value={n(block.later)}
                      hint={t('dash.progress.legendLaterHint')}
                    />
                  )}
                  {block.overdue > 0 && (
                    <li>
                      <Badge tone="error" className="tabular">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                        {t('dash.progress.overdueN', { n: n(block.overdue) })}
                      </Badge>
                    </li>
                  )}
                </ul>
              )}

              {block.kinds.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {block.kinds.map((row) => (
                    <KindRow key={row.kind} row={row} />
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 content-start gap-5 border-t hairline p-5 md:grid-cols-2 md:gap-8 xl:grid-cols-1 xl:gap-5 xl:border-s xl:border-t-0">
            <NextUp block={block} />
            <WeekStrip week={block.week} today={block.today} />
          </div>
        </div>
      </Card>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* The ring                                                                    */
/* -------------------------------------------------------------------------- */

const VIEW = 120;
const CENTER = VIEW / 2;
const RADIUS = 48;
const STROKE = 10;
/** A segment for each piece of work up to this many; past it, one continuous arc. */
const SEGMENTS_MAX = 36;
/** The dial's ticks around the ring: one an hour of a clock face. */
const TICKS = 12;

/**
 * The colours of the progress and hours cards (operator, 2026-11-02, shown four and
 * choosing their own: "Green for done, amber for met, red only when something is late,
 * quiet dark greys for the rest"). No gradient, no brand crimson: green what is done,
 * amber what is met — and so all done, and the hours past a target met — red what is
 * late or short, greys the rest. The hours card takes them from here.
 */
export const CARD_COLOURS = {
  doneStroke: 'stroke-success',
  lateStroke: 'stroke-error',
  restStroke: 'stroke-neutral-300 dark:stroke-neutral-700',
  done: 'bg-success',
  met: 'bg-warning',
  late: 'bg-error',
  rest: 'bg-neutral-300 dark:bg-neutral-700',
  /** Days gone by: the quiet dark grey. */
  past: 'bg-neutral-400 dark:bg-neutral-600',
} as const;

/** The closed ring's glow: amber, the colour of met, from the tokens. */
const GLOW = (() => {
  const hex = tokens.color.semantic.warning.base.slice(1);
  const [r, g, b] = [0, 2, 4].map((at) => parseInt(hex.slice(at, at + 2), 16));
  return `drop-shadow(0 0 10px rgba(${r}, ${g}, ${b}, 0.5))`;
})();

/** A point on a circle of radius `r`, at `deg` degrees clockwise from the top. */
function point(deg: number, r = RADIUS): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CENTER + r * Math.cos(rad), CENTER + r * Math.sin(rad)];
}

/** The ring from one angle to another, clockwise. A whole turn is a circle: an arc cannot end where it starts. */
function Arc({ from, to, className, style, stroke }: { from: number; to: number; className?: string; style?: CSSProperties; stroke?: string }) {
  if (to - from >= 359.99) {
    return <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" strokeWidth={STROKE} stroke={stroke} className={className} style={style} />;
  }
  const [x0, y0] = point(from);
  const [x1, y1] = point(to);
  const d = `M ${x0.toFixed(3)} ${y0.toFixed(3)} A ${RADIUS} ${RADIUS} 0 ${to - from > 180 ? 1 : 0} 1 ${x1.toFixed(3)} ${y1.toFixed(3)}`;
  return <path d={d} fill="none" strokeWidth={STROKE} stroke={stroke} className={className} style={style} />;
}

/** A finished piece lighting up: a fade, staggered round the ring, only where motion is welcome. */
const CHARGE = 'motion-safe:animate-[progress-charge_420ms_cubic-bezier(0.2,0,0,1)_both]';

/**
 * The day's work as a dial: a segment for each piece, the finished ones lit
 * green from the top round — clockwise, or the other way in Arabic, as a progress
 * bar runs — then the late ones red, then the rest grey. Past SEGMENTS_MAX
 * pieces it is one continuous arc. Closed, it glows amber.
 */
function ProgressRing({ block, complete }: { block: WorkProgressBlock; complete: boolean }) {
  const { t, locale } = useI18n();
  const total = block.done + block.left;
  const burst = useBurst(complete, block.today);

  const segments = total > 0 && total <= SEGMENTS_MAX;
  const step = segments ? 360 / total : 0;
  const gap = segments && total > 1 ? Math.min(4, step * 0.3) : 0;
  const cell = (i: number) => ({ from: i * step + gap / 2, to: (i + 1) * step - gap / 2 });
  const share = (count: number) => (total > 0 ? (count / total) * 360 : 0);

  const label =
    block.percent === null
      ? t('dash.progress.clear')
      : `${t('dash.progress.label', { done: formatNumber(block.done, locale), total: formatNumber(total, locale) })} — ${block.percent}%`;

  return (
    <div
      className="relative h-40 w-40 shrink-0 sm:h-44 sm:w-44"
      role="progressbar"
      aria-label={t('dash.progress.title')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={block.percent ?? undefined}
      aria-valuetext={label}
    >
      {/* The console texture behind the dial. */}
      <span className="absolute inset-3 rounded-full bg-grid-light bg-grid-size dark:bg-grid-dark" aria-hidden />
      <svg
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        className="relative h-full w-full flip-rtl transition-[filter] duration-slow ease-precision"
        style={complete ? { filter: GLOW } : undefined}
        aria-hidden
      >
        {/* The dial's ticks, outside the ring. */}
        {Array.from({ length: TICKS }, (_, i) => {
          const [x0, y0] = point((i * 360) / TICKS, RADIUS + STROKE / 2 + 2.5);
          const [x1, y1] = point((i * 360) / TICKS, RADIUS + STROKE / 2 + (i % 3 === 0 ? 6.5 : 4.5));
          return <line key={i} x1={x0} y1={y0} x2={x1} y2={y1} strokeWidth={1} className="stroke-[color:var(--line-strong)]" />;
        })}

        {/* The groove the segments sit in; what shows between them. */}
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" strokeWidth={STROKE} className="stroke-[color:var(--surface-sunken)]" />

        {segments ? (
          <>
            {Array.from({ length: total }, (_, i) => (
              <Arc
                key={`base-${i}`}
                {...cell(i)}
                className={clsx(
                  'transition-[stroke] duration-slow ease-precision',
                  i >= block.done && i < block.done + block.overdue ? CARD_COLOURS.lateStroke : CARD_COLOURS.restStroke,
                )}
              />
            ))}
            {Array.from({ length: block.done }, (_, i) => (
              <Arc key={`done-${i}`} {...cell(i)} className={clsx(CARD_COLOURS.doneStroke, CHARGE)} style={{ animationDelay: `${i * 40}ms` }} />
            ))}
          </>
        ) : (
          total > 0 && (
            <>
              <Arc from={0} to={360} className={CARD_COLOURS.restStroke} />
              {block.overdue > 0 && (
                <Arc from={share(block.done)} to={share(block.done + block.overdue)} className={CARD_COLOURS.lateStroke} />
              )}
              {block.done > 0 && <Arc from={0} to={share(block.done)} className={clsx(CARD_COLOURS.doneStroke, CHARGE)} />}
            </>
          )
        )}
      </svg>

      {/* The figure in the middle: not mirrored, as the dial is. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        {complete && (
          <span className={clsx('mb-0.5 grid h-6 w-6 place-items-center rounded-full text-ink', CARD_COLOURS.met)} aria-hidden>
            <Check className="h-4 w-4" strokeWidth={3} />
          </span>
        )}
        <span className={clsx('text-metric tabular', block.percent === null ? 'text-faint' : 'text-[var(--text-primary)]')}>
          {block.percent === null ? '—' : `${block.percent}%`}
        </span>
        {total > 0 && (
          <span className="max-w-[7.5rem] text-caption tabular text-muted">
            {t('dash.progress.of', { done: formatNumber(block.done, locale), total: formatNumber(total, locale) })}
          </span>
        )}
      </div>

      {burst && <Sparks />}
    </div>
  );
}

/** Off the ring when the day's work is all done: round it, amber and green, gone in under a second. */
const SPARKS = Array.from({ length: 14 }, (_, i) => {
  const deg = (i * 360) / 14 + (i % 2 ? 9 : 0);
  const [x, y] = point(deg, i % 2 ? 66 : 80);
  return {
    x: ((x - CENTER) * 176) / VIEW,
    y: ((y - CENTER) * 176) / VIEW,
    delay: (i % 4) * 60,
    color: [CARD_COLOURS.met, CARD_COLOURS.done, 'bg-signal-300'][i % 3],
  };
});

function Sparks() {
  return (
    <span className="pointer-events-none absolute inset-0" aria-hidden>
      {SPARKS.map((spark, i) => (
        <span
          key={i}
          className={clsx(
            'absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full opacity-0',
            'motion-safe:animate-[progress-spark_900ms_cubic-bezier(0,0,0.2,1)_both]',
            spark.color,
          )}
          style={{ '--spark-x': `${spark.x}px`, '--spark-y': `${spark.y}px`, animationDelay: `${spark.delay}ms` } as CSSProperties}
        />
      ))}
    </span>
  );
}

/** The browser's note of the last day it celebrated a day's work all done. */
const CELEBRATED_KEY = 'viganium.progress.celebrated';

/**
 * Whether to send off the sparks now: when the day's work comes to be all done
 * while the card is open, or the first time this browser sees it all done that
 * day — not on every visit to the dashboard after.
 */
function useBurst(complete: boolean, today: string): boolean {
  const [burst, setBurst] = useState(false);
  const was = useRef<boolean | null>(null);

  useEffect(() => {
    const before = was.current;
    was.current = complete;
    if (!complete) return;
    let seen = false;
    try {
      seen = window.localStorage.getItem(CELEBRATED_KEY) === today;
      window.localStorage.setItem(CELEBRATED_KEY, today);
    } catch {
      // Storage refused (a private window): celebrate as if for the first time.
    }
    if (!seen || before === false) setBurst(true);
  }, [complete, today]);

  // The sparks are gone in under a second; take them off the page after.
  useEffect(() => {
    if (!burst) return;
    const timer = window.setTimeout(() => setBurst(false), 1500);
    return () => window.clearTimeout(timer);
  }, [burst]);

  return burst;
}

/* -------------------------------------------------------------------------- */
/* Beside the ring                                                             */
/* -------------------------------------------------------------------------- */

/** One figure under the words — a swatch as on the ring, its count, and what it counts on hover. */
function LegendChip({ swatch, label, value, hint }: { swatch: string; label: string; value: string; hint: string }) {
  return (
    <li
      title={hint}
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill bg-neutral-500/12 px-2.5 py-0.5 text-caption ring-1 ring-inset ring-neutral-500/25"
    >
      <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', swatch)} aria-hidden />
      <span className="text-[var(--text-secondary)]">{label}</span>
      <span className="font-mono font-medium tabular text-[var(--text-primary)]">{value}</span>
      <span className="sr-only">{` — ${hint}`}</span>
    </li>
  );
}

/** Cells in a kind's bar up to this many; past it, a plain bar. */
const KIND_CELLS_MAX = 12;

/** One kind's done out of done and left: its menu's name, a bar of cells, the count. */
function KindRow({ row }: { row: WorkProgressBlock['kinds'][number] }) {
  const { t, locale } = useI18n();
  const Icon = KIND_ICONS[row.kind];
  const total = row.done + row.left;

  return (
    <li className="flex items-center gap-2.5">
      <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
      <Link
        to={KIND_LISTS[row.kind]}
        className="w-24 shrink-0 truncate text-start text-body-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
      >
        {t(KIND_LABELS[row.kind])}
      </Link>
      <span className="min-w-0 flex-1" aria-hidden>
        {total <= KIND_CELLS_MAX ? (
          <span className="flex gap-1">
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                className={clsx(
                  'h-2 min-w-0 flex-1 rounded-xs transition-colors duration-slow ease-precision',
                  i < row.done ? CARD_COLOURS.done : CARD_COLOURS.rest,
                )}
              />
            ))}
          </span>
        ) : (
          <ProgressBar percent={(row.done / total) * 100} tone="success" />
        )}
      </span>
      <span className="shrink-0 font-mono text-mono-sm tabular text-muted">
        {formatNumber(row.done, locale)}/{formatNumber(total, locale)}
      </span>
    </li>
  );
}

/** What to finish next, by name — the API's order: the late first, then the most urgent, then the nearest deadline. */
function NextUp({ block }: { block: WorkProgressBlock }) {
  const { t, locale, localized } = useI18n();

  return (
    <div className="min-w-0">
      <h3 className="text-overline uppercase text-[var(--text-tertiary)]">{t('dash.progress.next')}</h3>
      {block.next.length === 0 ? (
        <p className="mt-2 text-body-sm text-muted">{t('dash.progress.nothingNext')}</p>
      ) : (
        <ol className="mt-1.5 space-y-0.5">
          {block.next.map((item) => {
            const Icon = KIND_ICONS[item.kind];
            return (
              <li key={`${item.kind}:${item.id}`}>
                <Link
                  to={`${KIND_LINKS[item.kind]}/${item.id}`}
                  title={item.number ? `${item.number} — ${item.title}` : item.title}
                  className="-mx-2 flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors duration-fast hover:bg-[var(--surface-sunken)]"
                >
                  <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-baseline gap-1.5">
                      {item.number && (
                        <span className="shrink-0 font-mono text-mono-sm text-primary-600 dark:text-primary-400">{item.number}</span>
                      )}
                      <span className="truncate text-body-sm font-medium text-[var(--text-primary)]" dir="auto">
                        {item.title}
                      </span>
                    </span>
                    <span className="flex min-w-0 items-center gap-1.5 text-caption text-muted">
                      <span className="truncate">
                        {item.project ? localized(item.project) : t(KIND_LABELS[item.kind])}
                      </span>
                      <span aria-hidden>·</span>
                      <span
                        className={clsx(
                          'inline-flex shrink-0 items-center gap-1 tabular',
                          item.isOverdue && 'text-error-fg dark:text-error-dark',
                        )}
                      >
                        {item.isOverdue && <AlertTriangle className="h-3 w-3" aria-hidden />}
                        {item.isOverdue && <span className="sr-only">{t('tickets.overdue')}: </span>}
                        {item.dueAt ? formatDate(item.dueAt, locale) : enumLabel(item.status, locale)}
                      </span>
                    </span>
                  </span>
                  {(item.priority === 'URGENT' || item.priority === 'HIGH') && (
                    <Badge tone={priorityTone(item.priority)} className="shrink-0">
                      {enumLabel(item.priority, locale)}
                    </Badge>
                  )}
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Height, in px, of the tallest bar of the week. */
const BAR_MAX = 44;

/**
 * The last seven days as telemetry: a bar each, as tall as what was finished
 * that day against the week's best, today's lit. Oldest first — on the right in
 * Arabic, which reads from there.
 */
function WeekStrip({ week, today }: { week: WorkProgressBlock['week']; today: string }) {
  const { t, locale } = useI18n();
  const most = Math.max(1, ...week.map((day) => day.finished));
  const total = week.reduce((sum, day) => sum + day.finished, 0);

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-overline uppercase text-[var(--text-tertiary)]">{t('dash.progress.week')}</h3>
        <span className="font-mono text-mono-sm tabular text-muted">
          {t('dash.progress.weekTotal', { n: formatNumber(total, locale) })}
        </span>
      </div>
      <ol className="mt-2 grid grid-cols-7 gap-1.5">
        {week.map((day) => {
          const isToday = day.date === today;
          // Today by its weekday too, picked out in the brand's colour: "Today" does not fit a seventh of the strip.
          const name = formatWeekday(day.date, locale, locale === 'ar' ? 'narrow' : 'short');
          const said = t('dash.progress.dayFinished', {
            day: isToday
              ? t('dash.progress.today')
              : `${formatWeekday(day.date, locale, 'long')} ${formatDate(`${day.date}T12:00:00`, locale)}`,
            n: formatNumber(day.finished, locale),
          });
          return (
            <li key={day.date} title={said} className="flex min-w-0 flex-col items-center gap-1">
              <span className={clsx('font-mono text-mono-sm tabular', day.finished > 0 ? 'text-muted' : 'text-faint')} aria-hidden>
                {formatNumber(day.finished, locale)}
              </span>
              <span className="flex w-full items-end justify-center" style={{ height: BAR_MAX }} aria-hidden>
                <span
                  className={clsx(
                    'w-full max-w-[1.75rem] rounded-xs transition-[height] duration-slow ease-precision',
                    day.finished === 0 ? 'bg-[var(--line)]' : isToday ? CARD_COLOURS.done : CARD_COLOURS.past,
                  )}
                  style={{ height: day.finished === 0 ? 3 : Math.max(6, Math.round((day.finished / most) * BAR_MAX)) }}
                />
              </span>
              <span
                className={clsx(
                  'max-w-full truncate text-caption',
                  isToday ? 'font-semibold text-success-fg dark:text-success' : 'text-[var(--text-tertiary)]',
                )}
                aria-hidden
              >
                {name}
              </span>
              <span className="sr-only">{said}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
